import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, finalize } from 'rxjs/operators';
import { of, firstValueFrom } from 'rxjs';
import * as ExcelJS from 'exceljs';

import { LoggerService } from '../../../core/services/logger.service';
import {
    ControlDusService,
    ControlDusItem,
    DispatchGuideReportItem,
} from '../../../services/control-dus.service';
import { ControlDusNavigationService } from '../../../services/control-dus-navigation.service';

@Component({
    selector: 'app-control-dus-detalle',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        MatChipsModule,
        MatTooltipModule,
    ],
    templateUrl: './control-dus-detalle.component.html',
    styleUrl: './control-dus-detalle.component.scss',
})
export class ControlDusDetalleComponent implements OnInit {
    // ID de la instrucción de embarque (nroIE)
    nroIE: number = 0;

    // Datos del DUS
    dus: ControlDusItem | null = null;

    // Estado de carga
    isLoading: boolean = false;
    isGeneratingReporteGuias: boolean = false;
    loadingMessage: string = 'Cargando detalle del DUS...';
    error: string | null = null;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private controlDusService: ControlDusService,
        // eslint-disable-next-line no-unused-vars
        private controlDusNavigation: ControlDusNavigationService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {}

    ngOnInit(): void {
        // Obtener nroIE de los parámetros de la ruta
        const nroIEParam = this.route.snapshot.paramMap.get('nroIE');
        if (nroIEParam) {
            this.nroIE = parseInt(nroIEParam, 10);
            if (isNaN(this.nroIE)) {
                this.error = 'ID de instrucción de embarque inválido';
                this.logger.error(
                    `ID de instrucción de embarque inválido: ${nroIEParam}`,
                    new Error('Invalid nroIE'),
                    'ControlDusDetalleComponent'
                );
                return;
            }
            this.cargarDetalle();
        } else {
            this.error = 'No se proporcionó ID de instrucción de embarque';
            this.logger.error(
                'No se proporcionó nroIE en los parámetros de la ruta',
                new Error('Missing nroIE'),
                'ControlDusDetalleComponent'
            );
        }
    }

    /**
     * Carga el detalle del DUS desde el backend
     */
    cargarDetalle(): void {
        this.isLoading = true;
        this.error = null;
        this.loadingMessage = 'Cargando detalle del DUS...';

        this.logger.info(
            `Cargando detalle de Control DUS: nroIE=${this.nroIE}`,
            'ControlDusDetalleComponent'
        );

        this.controlDusService
            .getControlDusDetalle(this.nroIE)
            .pipe(
                catchError((error) => {
                    this.logger.error(
                        'Error al cargar detalle de Control DUS',
                        error as Error,
                        'ControlDusDetalleComponent'
                    );
                    const errorMessage =
                        error?.error?.message ||
                        'Error al cargar el detalle del DUS';
                    this.error = errorMessage;
                    this.snackBar.open(errorMessage, 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((response) => {
                if (response && response.success && response.data) {
                    this.dus = response.data;
                    this.logger.info(
                        `Detalle de Control DUS cargado exitosamente: nroIE=${this.nroIE}`,
                        'ControlDusDetalleComponent'
                    );
                } else {
                    this.error = 'No se encontró el DUS solicitado';
                    this.logger.warn(
                        `DUS no encontrado: nroIE=${this.nroIE}`,
                        'ControlDusDetalleComponent'
                    );
                }
            });
    }

    /**
     * Navega de vuelta al listado de Control DUS manteniendo filtros y paginación
     */
    volver(): void {
        this.controlDusNavigation.backToList();
    }

    /**
     * Navega a la vista Master-Detail de Packing Lists para este DUS
     */
    verPackingLists(): void {
        if (this.dus && this.dus.dus) {
            // route parameter dus is what is called customsLoadReference
            this.router.navigate([
                '/control-dus',
                this.nroIE,
                'packing-list',
                this.dus.dus,
            ]);
        } else {
            this.snackBar.open(
                'No hay número de DUS asociado para ver Packing Lists',
                'Cerrar',
                { duration: 3000 }
            );
        }
    }

    /**
     * Genera y descarga el Reporte de Guías de Despacho para este DUS.
     */
    async exportarReporteGuias(): Promise<void> {
        this.isGeneratingReporteGuias = true;
        this.logger.info(
            `Exportando Reporte Guías de Despacho: nroIE=${this.nroIE}`,
            'ControlDusDetalleComponent'
        );

        try {
            const data = await firstValueFrom(
                this.controlDusService.getDispatcherReportByNroIE(this.nroIE)
            );

            if (!data?.length) {
                this.snackBar.open(
                    'No hay guías de despacho para este DUS',
                    'Cerrar',
                    {
                        duration: 3000,
                        panelClass: ['warning-snackbar'],
                    }
                );
                return;
            }

            await this.generarExcelReporteGuias(data);
            this.snackBar.open('Reporte generado correctamente', 'Cerrar', {
                duration: 3000,
                panelClass: ['success-snackbar'],
            });
        } catch (error) {
            this.logger.error(
                'Error al generar Reporte Guías',
                error as Error,
                'ControlDusDetalleComponent'
            );
            this.snackBar.open('Error al generar el reporte', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            this.isGeneratingReporteGuias = false;
        }
    }

    private async generarExcelReporteGuias(
        data: DispatchGuideReportItem[]
    ): Promise<void> {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Guías de Despacho');

        const colors = {
            primary: '1e3a8a',
            dark: '1e293b',
            gray: '64748b',
            light: 'f1f5f9',
        };

        try {
            const baseHref =
                document.querySelector('base')?.getAttribute('href') || '/';
            const logoResponse = await fetch(
                `${baseHref}assets/images/logos/logotipo-fondo-claro.png`
            );
            if (logoResponse.ok) {
                const logoBuffer = await logoResponse.arrayBuffer();
                const logoId = workbook.addImage({
                    buffer: logoBuffer,
                    extension: 'png',
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0.2, row: 0.2 },
                    ext: { width: 150, height: 50 },
                });
            }
        } catch {
            /* logo opcional */
        }

        const titleCell = worksheet.getCell('D1');
        titleCell.value = `REPORTE GUÍAS DE DESPACHO — I/E ${String(
            this.nroIE
        ).padStart(8, '0')}`;
        titleCell.font = {
            name: 'Calibri',
            size: 16,
            bold: true,
            color: { argb: 'FF' + colors.primary },
        };
        titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D1:AB1');
        worksheet.getRow(1).height = 40;

        const dateCell = worksheet.getCell('D2');
        dateCell.value = `Generado el: ${new Date().toLocaleDateString(
            'es-CL',
            {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            }
        )}`;
        dateCell.font = {
            name: 'Calibri',
            size: 11,
            color: { argb: 'FF' + colors.gray },
        };
        dateCell.alignment = { vertical: 'middle', horizontal: 'left' };
        worksheet.mergeCells('D2:AB2');
        worksheet.getRow(2).height = 25;
        worksheet.getRow(3).height = 10;

        const columnas = [
            {
                header: 'Referencia',
                key: 'referencia',
                width: 16,
                numeric: false,
            },
            { header: 'Cliente', key: 'cliente', width: 22, numeric: false },
            {
                header: 'Exportador',
                key: 'exportador',
                width: 20,
                numeric: false,
            },
            { header: 'Guía', key: 'guia', width: 18, numeric: false },
            {
                header: 'Estado Guía',
                key: 'estadoGuia',
                width: 16,
                numeric: false,
            },
            { header: 'Bodega', key: 'bodega', width: 20, numeric: false },
            { header: 'Nivel', key: 'nivel', width: 10, numeric: false },
            { header: 'Patente', key: 'patente', width: 14, numeric: false },
            { header: 'Nave', key: 'nave', width: 22, numeric: false },
            { header: 'Booking', key: 'booking', width: 16, numeric: false },
            { header: 'POL', key: 'pol', width: 10, numeric: false },
            { header: 'ETD', key: 'etd', width: 12, numeric: false },
            { header: 'POD', key: 'pod', width: 10, numeric: false },
            {
                header: 'Consignatario',
                key: 'consignatario',
                width: 22,
                numeric: false,
            },
            { header: 'DUS', key: 'dus', width: 16, numeric: false },
            {
                header: 'Contenedor',
                key: 'contenedor',
                width: 16,
                numeric: false,
            },
            {
                header: 'Cant. Cntrs',
                key: 'cantidadCntrs',
                width: 12,
                numeric: true,
            },
            {
                header: 'Tipo Embarque',
                key: 'tipoEmbarque',
                width: 14,
                numeric: false,
            },
            {
                header: 'Cant. Pallets',
                key: 'cantidadPallets',
                width: 13,
                numeric: true,
            },
            { header: 'Despacho', key: 'despacho', width: 14, numeric: false },
            { header: 'Visación', key: 'visacion', width: 14, numeric: false },
            {
                header: 'Ingreso Puerto',
                key: 'ingresoPuerto',
                width: 14,
                numeric: false,
            },
            { header: 'Stacking', key: 'stacking', width: 14, numeric: false },
            {
                header: 'Liquidación',
                key: 'liquidacion',
                width: 14,
                numeric: false,
            },
            {
                header: 'Salida Puerto',
                key: 'salidaPuerto',
                width: 14,
                numeric: false,
            },
            {
                header: 'Estadía Puerto',
                key: 'estadiaPuerto',
                width: 13,
                numeric: true,
            },
            {
                header: 'Comentarios',
                key: 'comentarios',
                width: 30,
                numeric: false,
            },
        ];

        columnas.forEach((col, i) => {
            worksheet.getColumn(i + 1).width = col.width;
        });

        const headerRow = worksheet.getRow(4);
        headerRow.height = 30;
        columnas.forEach((col, i) => {
            const cell = worksheet.getCell(4, i + 1);
            cell.value = col.header;
            cell.font = {
                name: 'Calibri',
                size: 12,
                bold: true,
                color: { argb: 'FFFFFFFF' },
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF' + colors.primary },
            };
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true,
            };
            cell.border = {
                top: { style: 'medium', color: { argb: 'FF' + colors.dark } },
                bottom: {
                    style: 'medium',
                    color: { argb: 'FF' + colors.dark },
                },
                left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
                right: { style: 'thin', color: { argb: 'FFFFFFFF' } },
            };
        });

        const fmtFecha = (f: string | null | undefined): string => {
            if (!f) return '-';
            const d = new Date(f);
            if (isNaN(d.getTime())) return '-';
            return d.toLocaleString('es-CL', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'UTC',
            });
        };

        data.forEach((item, index) => {
            const rowNum = 5 + index;
            const row = worksheet.getRow(rowNum);
            row.height = 22;
            const fillColor =
                index % 2 === 0
                    ? { argb: 'FFFFFFFF' }
                    : { argb: 'FF' + colors.light };

            const values: (string | number | null)[] = [
                item.referencia || '-',
                item.cliente || '-',
                item.exportador || '-',
                item.guia || '-',
                item.estadoGuia || '-',
                item.bodega || '-',
                '', // Nivel (no disponible en la vista)
                item.patente || '-',
                item.nave || '-',
                item.booking || '-',
                item.pol || '-',
                fmtFecha(item.etd),
                item.pod || '-',
                item.consignatario || '-',
                item.dus || '-',
                item.contenedor || '-',
                item.cantidadCntrs ?? '-',
                item.tipoEmbarque || '-',
                item.cantidadPallets ?? '-',
                fmtFecha(item.despacho),
                fmtFecha(item.visacion),
                fmtFecha(item.ingresoPuerto),
                fmtFecha(item.stacking),
                fmtFecha(item.liquidacion),
                fmtFecha(item.salidaPuerto),
                item.estadiaPuerto ?? '-',
                item.comentarios || '-',
            ];

            values.forEach((val, colIdx) => {
                const cell = row.getCell(colIdx + 1);
                cell.value = val as ExcelJS.CellValue;
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: fillColor,
                };
                cell.font = {
                    name: 'Calibri',
                    size: 11,
                    color: { argb: 'FF000000' },
                };
                cell.alignment = {
                    vertical: 'middle',
                    horizontal: columnas[colIdx].numeric ? 'right' : 'left',
                };
                cell.border = {
                    top: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    bottom: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    left: { style: 'thin', color: { argb: 'FFd1d5db' } },
                    right: { style: 'thin', color: { argb: 'FFd1d5db' } },
                };
                if (columnas[colIdx].numeric && typeof val === 'number') {
                    cell.numFmt = '#,##0';
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ReporteGuias_IE${this.nroIE}_${new Date()
            .toISOString()
            .replace(/[-:.T]/g, '')
            .slice(0, 14)}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Formatea un número con padding (similar a padNumber en service-order-detail)
     */
    padNumber(num: number): string {
        return String(num).padStart(8, '0');
    }

    /**
     * Formatea una fecha para mostrar (igual que en service-order-detail)
     */
    formatearFecha(fecha: Date | string | undefined): string {
        if (!fecha) return '-';
        try {
            const dateObj = typeof fecha === 'string' ? new Date(fecha) : fecha;
            if (isNaN(dateObj.getTime())) return '-';
            return new Intl.DateTimeFormat('es-CL', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            }).format(dateObj);
        } catch {
            return '-';
        }
    }

    /**
     * Formatea un número para mostrar
     */
    formatearNumero(valor: number | undefined): string {
        if (valor === null || valor === undefined) return '0';
        return valor.toLocaleString('es-CL');
    }

    /**
     * Calcula los días de tránsito ESTIMADOS (Voyage time) entre ETD y ETA.
     * Definición: Diferencia entre ETD y ETA.
     * Retorna '-' si los datos son inválidos (ETA antes de ETD).
     */
    calcularDiasTransito(
        etd: Date | string | undefined,
        eta: Date | string | undefined
    ): number | string {
        if (!etd || !eta) return '-';
        try {
            const fechaEtd = typeof etd === 'string' ? new Date(etd) : etd;
            const fechaEta = typeof eta === 'string' ? new Date(eta) : eta;
            if (isNaN(fechaEtd.getTime()) || isNaN(fechaEta.getTime()))
                return '-';
            const diffTime = fechaEta.getTime() - fechaEtd.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            // Retornar '-' si el resultado es negativo (datos inválidos: ETA < ETD)
            return diffDays >= 0 ? diffDays : '-';
        } catch {
            return '-';
        }
    }
    /**
     * Calcula los días transcurridos desde ETD (Control DUS).
     * Definición del Listado: Cuenta los días transcurridos desde el ETD.
     * Se detiene la cuenta cuando el DUS pasa a estado Finalizado.
     */
    calcularDiasTranscurridos(
        etd: Date | string | undefined,
        estado: string | undefined,
        diasFinalizado?: number
    ): number | string {
        if (!etd) return '-';

        // Si está finalizado, usar el valor guardado del backend o '-'
        if (estado === 'Finalizado') {
            return diasFinalizado ?? '-';
        }

        try {
            const fechaEtd = typeof etd === 'string' ? new Date(etd) : etd;
            if (isNaN(fechaEtd.getTime())) return '-';

            const today = new Date();
            const diffTime = today.getTime() - fechaEtd.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            return diffDays >= 0 ? diffDays : '-';
        } catch {
            return '-';
        }
    }
    /**
     * Obtiene el color del badge según el estado
     */
    getEstadoColor(estado: string): string {
        switch (estado?.toLowerCase()) {
            case 'pendiente':
                return 'warning';
            case 'en proceso':
                return 'primary';
            case 'finalizado':
                return 'success';
            default:
                return '';
        }
    }

    /**
     * Obtiene el color del badge según la etapa
     */
    getEtapaColor(etapa: string | undefined): string {
        if (!etapa) return '';
        switch (etapa.toLowerCase()) {
            case 'legalización':
                return 'onHand';
            case 'documentación':
                return 'primary';
            default:
                return '';
        }
    }
}
